import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useSecurity } from "../context/SecurityContext";
import { adminSetPin } from "../lib/api";

// Shown once right after the Administrator signs in with an emailed code: pick a new PIN, no old PIN needed.
export const SetPinPrompt = () => {
  const { pinSetup, clearPinSetup, member } = useSecurity();
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  if (!pinSetup || member?.role !== "admin") return null;
  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) return toast.error("PIN must be 4–6 digits");
    if (pin !== again) return toast.error("The two PINs don't match");
    setBusy(true);
    try {
      await adminSetPin(pin);
      toast.success("New PIN saved — use it next time you unlock");
      clearPinSetup();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the PIN");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && clearPinSetup()}>
      <DialogContent className="sm:max-w-md" data-testid="set-pin-prompt">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Pick a new PIN</DialogTitle>
          <DialogDescription>You signed in with an emailed code, so you can set a fresh PIN now without the old one. This offer lasts 15 minutes.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input data-testid="set-pin-new-input" inputMode="numeric" type="password" maxLength={6} autoFocus value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="New PIN (4–6 digits)" />
          <Input data-testid="set-pin-again-input" inputMode="numeric" type="password" maxLength={6} value={again} onChange={(e) => setAgain(e.target.value.replace(/\D/g, ""))} placeholder="Type it again" />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={clearPinSetup} data-testid="set-pin-skip-btn">Keep my current PIN</Button>
            <Button type="submit" disabled={busy || pin.length < 4} data-testid="set-pin-save-btn" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Save new PIN</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
